import React, { ChangeEvent, FC, useCallback, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { FormGroup, Input, Label, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { BadgeDesign } from '../../../shared/user/badgeDesign';
import {
  InputMultiSelect,
  MaterialIcon,
  MultiSelectDirection,
  MultiSelectItem,
  RichMarkdownIt,
} from '../../components';
import { TranslationKey, useTranslation } from '../../translations';
import { displayName, Fetcher, useConvention, useFetcher } from '../../utils';
import { CashierContext, PrinterPreferenceMode } from './cashiercontext';

import './settingsModal.scss';

export const CashierSettings: FC = () => {
  const cashierSettings = useContext(CashierContext);
  const [modalOpen, setModalOpen] = useState(false);

  const open = useCallback(() => {
    setModalOpen(true);
    cashierSettings.updateCashierSettings({
      enableESCHotkey: false,
    });
  }, [setModalOpen, cashierSettings]);

  const close = useCallback(() => {
    setModalOpen(false);
    cashierSettings.updateCashierSettings({
      enableESCHotkey: true,
    });
  }, [setModalOpen, cashierSettings]);

  return (
    <div className="cashier-settings">
      <MaterialIcon name="settings" onClick={open} type="muted" />
      {modalOpen && <CashierSettingsModal close={close} />}
    </div>
  );
};

interface ModalProps {
  readonly close: () => void;
}

const CashierSettingsModal: FC<ModalProps> = ({ close }) => {
  const { ts } = useTranslation();
  return (
    <Modal
      className="modal-large cashier-settings-modal hide-print"
      fade={false}
      isOpen
      toggle={close}
    >
      <ModalHeader toggle={close}>{ts('configure_registration_station')}</ModalHeader>
      <ModalBody>
        <UpdateCashierSettings />
      </ModalBody>
    </Modal>
  );
};

const UpdateCashierSettings: FC = () => {
  const cs = useContext(CashierContext);
  const convention = useConvention();

  const { ts } = useTranslation();
  return (
    <div className="cashier-settings-form">
      <div className="cashier-settings-form-item">
        <CashierSettingsToggle
          defaultChecked={cs.config.enablePayments}
          id="accept-payments"
          label="cashier.acceptance_enabled"
          name="accept-payments"
          onChange={(checked) => {
            cs.updateCashierSettings({ enablePayments: checked });
          }}
        />
        <div className="cashier-form-item-description">
          <p>{ts('if_enabled_this_registration_station_any_balance')}</p>
          <p>{ts('otherwise_cashiers_will_be_informed')}</p>
        </div>
      </div>
      <div className="cashier-settings-form-item">
        <CashierSettingsToggle
          defaultChecked={cs.config.requireIDScan}
          id="require-id-check"
          label="cashier.no_id_scanned"
          name="require-id-check"
          onChange={(checked) => {
            cs.updateCashierSettings({ requireIDScan: checked });
          }}
        />
        <div className="cashier-form-item-description">
          <p>{ts('if_enabled_this_registration_station_require_scan')}</p>
          <p>{ts('otherwise_cashiers_will_be_informed')}</p>
        </div>
      </div>
      <div className="cashier-settings-form-item">
        <CashierSettingsToggle
          defaultChecked={cs.config.enablePrintingStaffBadges}
          id="enable-staff-checkin"
          label="cashier.enable_staff_check_in"
          name="enable-staff-checkin"
          onChange={(checked) => {
            cs.updateCashierSettings({ enablePrintingStaffBadges: checked });
          }}
        />
        <div className="cashier-form-item-description">
          <p>{ts('if_enabled_this_registration_station_print_staff')}</p>
          <p>{ts('otherwise_cashiers_will_be_informed')}</p>
        </div>
      </div>
      <CashierRestrictionsPreference />
      <div className="cashier-settings-form-item">
        <CashierSettingsToggle
          defaultChecked={cs.config.printSeparateStaffBadge}
          id="print-separate-staff-badge"
          label="cashier.print_separate_staff"
          name="print-separate-staff-badge"
          onChange={(checked) => {
            cs.updateCashierSettings({ printSeparateStaffBadge: checked });
          }}
        />
        <div className="cashier-form-item-description">
          <RichMarkdownIt source={ts('if_enabled_this_registration_station_print_staff_extra')} />
        </div>
      </div>
      <div className="cashier-settings-form-item">
        <CashierSettingsToggle
          defaultChecked={cs.config.printSeparateVendorBadge}
          id="print-separate-vendor-badge"
          label="cashier.print_separate_vendor"
          name="print-separate-vendor-badge"
          onChange={(checked) => {
            cs.updateCashierSettings({ printSeparateVendorBadge: checked });
          }}
        />
        <div className="cashier-form-item-description">
          <RichMarkdownIt source={ts('if_enabled_this_registration_station_vendor')} />
        </div>
      </div>
      <div className="cashier-settings-form-item">
        <CashierSettingsToggle
          defaultChecked={cs.config.showReprintModal}
          disabled={convention.forceReprintReason}
          id="print-show-reprint-modal"
          label="cashier.show_reprint_modal"
          name="print-show-reprint-modal"
          onChange={(checked) => {
            if (convention.forceReprintReason) {
              return;
            }

            cs.updateCashierSettings({ showReprintModal: checked });
          }}
        />
        <div className="cashier-form-item-description">
          <RichMarkdownIt source={ts('cashier_settings.reprint_modal')} />
          {convention.forceReprintReason && (
            <span className="text-danger">{ts('cashier_settings.reprint_modal.disabled')}</span>
          )}
        </div>
      </div>
      <div className="cashier-settings-form-item">
        <div className="cashier-form-item-input me-3">
          <StationLevelBadgeOverride
            designOverrideId={cs.config.overrideDesign?.id ?? -1}
            onChange={(design) => {
              cs.updateCashierSettings({ overrideDesign: design });
            }}
          />
        </div>
        <div className="cashier-form-item-description">
          <RichMarkdownIt source="Override all prints with the selected badge design, **regardless of attendance level.** (dangerous)" />
        </div>
      </div>
      <div className="cashier-settings-form-item">
        <CashierBadgeTypePreference />
      </div>
    </div>
  );
};

const StationLevelBadgeOverride: FC<{
  readonly onChange: (design: BadgeDesign | undefined) => void;
  readonly designOverrideId: number;
}> = ({ onChange, designOverrideId = -1 }) => {
  const designFetcher = useFetcher<BadgeDesign[]>(async () => {
    return await api.getBadgeDesignList();
  });

  const { ts } = useTranslation();

  const [showSaved, setShowSaved] = useState(false);

  const internalOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = Number.parseInt(e.target.value, 10);

      if (value === -1) {
        onChange(undefined);
      } else {
        if (!designFetcher.data) {
          return;
        }

        const newDesign = designFetcher.data.find(
          (design) => design.id === Number.parseInt(e.target.value, 10),
        );

        if (!newDesign) {
          toast.error(ts('bad_badge_design_please_refresh'));
          return;
        }

        onChange(newDesign);
      }

      setShowSaved(true);

      setTimeout(() => {
        setShowSaved(false);
      }, 2500);
    },
    [onChange, designFetcher.data, ts],
  );

  if (!designFetcher.success) {
    return <Fetcher inline result={designFetcher} />;
  }

  return (
    <>
      <Input
        defaultValue={designOverrideId}
        id="designOverrideSelect"
        onChange={internalOnChange}
        type="select"
      >
        <option value={-1}>({ts('default')})</option>
        {designFetcher.data.map((design) => (
          <option key={design.id} value={design.id}>
            {design.name}
          </option>
        ))}
      </Input>
      {showSaved && (
        <div className="cashier-form-item-saved" id="enablePayments-saved">
          {ts('saved')}!
        </div>
      )}
    </>
  );
};

interface ToggleProps {
  readonly defaultChecked: boolean;
  readonly id: string;
  readonly label: TranslationKey;
  readonly name: string;
  readonly disabled?: boolean;
  readonly onChange: (checked: boolean) => void;
}

const CashierSettingsToggle: FC<ToggleProps> = ({
  defaultChecked,
  id,
  label,
  name,
  disabled,
  onChange,
}) => {
  const [showSaved, setShowSaved] = useState(false);
  const { ts } = useTranslation();

  const internalOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (disabled) {
        return;
      }

      onChange(e.target.checked);
      setShowSaved(true);

      setTimeout(() => {
        setShowSaved(false);
      }, 2500);
    },
    [disabled, onChange],
  );

  return (
    <div className="cashier-form-item-input">
      <FormGroup switch>
        <Input
          className="form-switch-input"
          defaultChecked={defaultChecked}
          disabled={disabled}
          id={id}
          name={name}
          onChange={internalOnChange}
          type="switch"
        />
        <Label check>{ts(label)}</Label>
      </FormGroup>
      {showSaved && (
        <div className="cashier-form-item-saved" id="enablePayments-saved">
          {ts('saved')}!
        </div>
      )}
    </div>
  );
};

const CashierBadgeTypePreference: FC = () => {
  const { ts } = useTranslation();
  const [showSaved, setShowSaved] = useState(false);
  const cashierSettings = useContext(CashierContext);

  const primary: MultiSelectItem[] = cashierSettings.config.badgePrintPreferences
    .filter((b) => b.preference === PrinterPreferenceMode.Primary)
    .map((b) => ({ value: b.text, id: b.id }));

  const secondary: MultiSelectItem[] = cashierSettings.config.badgePrintPreferences
    .filter((b) => b.preference === PrinterPreferenceMode.Secondary)
    .map((b) => ({ value: b.text, id: b.id }));

  const onMove = useCallback(
    (items: MultiSelectItem[], type: MultiSelectDirection) => {
      cashierSettings.updateCashierSettings({
        badgePrintPreferences: cashierSettings.config.badgePrintPreferences.map((b) => {
          if (items.some((i) => i.id === b.id)) {
            return {
              ...b,
              preference:
                type === MultiSelectDirection.Left
                  ? PrinterPreferenceMode.Primary
                  : PrinterPreferenceMode.Secondary,
            };
          }

          return b;
        }),
      });

      setShowSaved(true);

      setTimeout(() => {
        setShowSaved(false);
      }, 2500);
    },
    [cashierSettings],
  );

  return (
    <div className="cashier-badgetype-preferences">
      <div className="cashier-badgetype-header">
        <div>{ts('badge_type_printer_preferences')}</div>
        {showSaved && (
          <div className="cashier-form-item-saved" id="badgeTypePreferences-saved">
            {ts('saved')}!
          </div>
        )}
      </div>
      <p className="cashier-explanation-text">{ts('select_the_printers_that_should')}</p>
      <InputMultiSelect
        leftItems={primary}
        leftLabel={ts('cashier.primary_printer')}
        onMove={onMove}
        rightItems={secondary}
        rightLabel={ts('cashier.secondary_printer')}
      />
    </div>
  );
};

const CashierRestrictionsPreference: FC = () => {
  const { ts } = useTranslation();
  const cs = useContext(CashierContext);
  const products = useFetcher(async () => {
    return await api.getProducts();
  }, []);

  const onSelectChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const selectedItems: number[] = Array.from(e.target.selectedOptions).map((option) => {
        return Number.parseInt(option.value, 10);
      });

      cs.updateCashierSettings({ checkinProducts: selectedItems });
    },
    [cs],
  );

  if (!products.success) {
    return <Fetcher inline result={products} />;
  }

  return (
    <div>
      <div className="cashier-settings-form-item">
        <CashierSettingsToggle
          defaultChecked={cs.config.checkinProducts !== undefined}
          id="restrict-product-checkin"
          label="cashier.restrict_product_checkin"
          name="restrict-product-checkin"
          onChange={(checked) => {
            cs.updateCashierSettings({ checkinProducts: checked ? [] : undefined });
          }}
        />
        <div className="cashier-form-item-description">
          <p>{ts('cashier.checkin_only_products_description')}</p>
        </div>
      </div>
      {cs.config.checkinProducts && (
        <div className="cashier-settings-form-item">
          <div className="cashier-form-item-input">
            <select className="form-select" multiple onChange={onSelectChange}>
              {products.data
                .filter((t) => t.category.isRegistration)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((item) => (
                  <option
                    key={item.id}
                    selected={cs.config.checkinProducts?.includes(item.id)}
                    value={item.id}
                  >
                    {displayName(item)}
                  </option>
                ))}
            </select>
          </div>
          <div className="cashier-form-item-description">
            <p>{ts('cashier.select_products_description')}</p>
          </div>
        </div>
      )}
    </div>
  );
};
